import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { LuEllipsisVertical } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { HouseholdType, OpportunityView } from '@ndvr/customer-api';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDockState } from '@/views/dock/dock-state';
import { formatDate } from 'utils';
import { CustomTablePage } from 'widgets';

import { useOpportunityOverlayState } from '../opportunity-state';
import { OpportunityFormType, toOpportunityFormValues } from './OpportunityForm';
import { OpportunityStatusBadge } from './OpportunityStatusBadge';

const columnHelper = createColumnHelper<OpportunityView>();

interface OpportunityListViewProps {
    opportunities: OpportunityView[];
    isLoading?: boolean;
    householdMap?: Map<string, { name: string; type?: HouseholdType }>;
    showHousehold?: boolean;
}

export function OpportunityListView({
    opportunities,
    isLoading,
    householdMap,
    showHousehold,
}: OpportunityListViewProps) {
    const { openViewOpportunitySheet: openView } = useOpportunityOverlayState();

    const columns = React.useMemo(
        () => [
            columnHelper.accessor((row) => row.name || 'Untitled Opportunity', {
                id: 'name',
                header: 'Name',
                cell: ({ row }) => (
                    <span className="block max-w-[200px] truncate">{row.original.name || 'Untitled Opportunity'}</span>
                ),
            }),
            ...(showHousehold
                ? [
                      columnHelper.accessor(
                          (row) => {
                              const entry = householdMap?.get(row.householdGUID);
                              return (
                                  entry?.name ||
                                  (entry?.type === HouseholdType.PROSPECT ? 'Unknown Prospect' : 'Unknown Household')
                              );
                          },
                          {
                              id: 'household',
                              header: 'Prospect / Household',
                              cell: ({ row }) => {
                                  const entry = householdMap?.get(row.original.householdGUID);
                                  const type = entry?.type;
                                  const name =
                                      entry?.name ||
                                      (type === HouseholdType.PROSPECT ? 'Unknown Prospect' : 'Unknown Household');
                                  return (
                                      <Link
                                          to={`/${type === HouseholdType.PROSPECT ? 'prospect' : 'household'}/${row.original.householdGUID}`}
                                          className="block max-w-[200px] truncate hover:underline"
                                          onClick={(e) => e.stopPropagation()}
                                      >
                                          {name}
                                      </Link>
                                  );
                              },
                          },
                      ),
                  ]
                : []),
            columnHelper.accessor('description', {
                header: 'Description',
                cell: ({ row }) => (
                    <span className="block max-w-[300px] truncate">{row.original.description || '—'}</span>
                ),
            }),
            columnHelper.accessor('value', {
                header: 'Value',
                cell: ({ row }) => {
                    const value = row.original.value;
                    return value ? (
                        <span className="font-medium">
                            ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                        </span>
                    ) : (
                        <span>—</span>
                    );
                },
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: ({ row }) => (
                    <div className="max-w-[160px]">
                        <OpportunityStatusBadge status={row.original.status} />
                    </div>
                ),
            }),
            columnHelper.accessor('createdAt', {
                header: 'Created',
                cell: ({ row }) => <span>{formatDate(row.original.createdAt)}</span>,
            }),
            columnHelper.accessor('updatedAt', {
                header: 'Updated',
                cell: ({ row }) => <span>{formatDate(row.original.updatedAt)}</span>,
            }),
            columnHelper.display({
                id: 'actions',
                header: '',
                cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <OpportunityActions opportunity={row.original} />
                    </div>
                ),
                size: 48,
                minSize: 48,
                meta: { isDisplayCol: true },
            }),
        ],
        [householdMap, showHousehold],
    );

    return (
        <CustomTablePage
            data={opportunities}
            columns={columns}
            isLoading={isLoading}
            rowOnClick={(opportunity) => openView(opportunity.opportunityGUID)}
            exportName="opportunities"
            defaultSortCol="createdAt"
            defaultSortDirection="desc"
            cardClassName="m-0 border-0 shadow-none"
        />
    );
}

interface OpportunityActionsProps {
    opportunity: OpportunityView;
}

const OpportunityActions = ({ opportunity }: OpportunityActionsProps) => {
    const { openViewOpportunitySheet: openView, openDeleteOpportunityDialog: openDelete } =
        useOpportunityOverlayState();
    const { addUpdateOpportunityDockItem } = useDockState();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
                <div className="hover:bg-ndvr-seafoam/50 hover:text-ndvr-dark inline-flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium">
                    <LuEllipsisVertical className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openView(opportunity.opportunityGUID)}>View</DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => {
                        addUpdateOpportunityDockItem(
                            opportunity.opportunityGUID,
                            opportunity.householdGUID,
                            toOpportunityFormValues(opportunity) as OpportunityFormType,
                        );
                    }}
                >
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openDelete(opportunity.opportunityGUID)}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
